/**
 * Step-by-step instructions written as consecutive "### Schritt 1",
 * "### Schritt 2", ... sections (as the Rise import writes them, and as an
 * author may type them) are shown as a carousel. A Rise course of several
 * lessons puts each lesson under "##" and its steps one level further down,
 * at "####", so both levels count. This finds those runs in an
 * article's Markdown.
 *
 * A run is two or more step headings in a row with rising numbers; a step's
 * content reaches to the next heading of level 1-3 or the end of the text,
 * and numbering that starts over begins a new run. Anything else - a
 * single "Schritt" heading, or steps interrupted by another section - stays
 * ordinary text. Fenced code blocks are left alone.
 */

const STEP_HEADING = /^#{3,4}\s+(?:Schritt|Step)\s+(\d+)\s*(?:[:.–-]\s*(.*?))?\s*#*\s*$/i;
const SECTION_HEADING = /^#{1,3}\s+\S/;
const FENCE = /^\s*(```|~~~)/;

/**
 * Splits Markdown into segments:
 *   { type: 'markdown', text }
 *   { type: 'steps', steps: [{ number, title, text }] }
 */
export function splitStepGroups(markdown = '') {
  const lines = markdown.split(/\r?\n/);
  const segments = [];
  let buffer = [];
  let group = null;
  let inFence = false;

  function flushText() {
    if (buffer.length) {
      segments.push({ type: 'markdown', text: buffer.join('\n') });
      buffer = [];
    }
  }

  function closeGroup() {
    if (!group) {
      return;
    }

    if (group.length >= 2) {
      flushText();
      segments.push({
        type: 'steps',
        steps: group.map((step) => ({
          number: step.number,
          title: step.title,
          heading: step.heading,
          text: step.lines.join('\n').trim(),
        })),
      });
    } else {
      // A lone "Schritt" heading is just a heading.
      for (const step of group) {
        buffer.push(step.raw, ...step.lines);
      }
    }

    group = null;
  }

  for (const line of lines) {
    if (FENCE.test(line)) {
      inFence = !inFence;
    }

    const step = !inFence && STEP_HEADING.exec(line);

    if (step) {
      // Numbering that starts over ("Schritt 1" again) begins a new run.
      if (group && Number(step[1]) <= group[group.length - 1].number) {
        closeGroup();
      }

      if (!group) {
        group = [];
      }

      group.push({
        number: Number(step[1]),
        title: (step[2] ?? '').trim(),
        heading: line.replace(/^#{3,4}\s+/, '').replace(/\s*#*\s*$/, ''),
        raw: line,
        lines: [],
      });
      continue;
    }

    if (group && !inFence && SECTION_HEADING.test(line)) {
      closeGroup();
    }

    if (group) {
      group[group.length - 1].lines.push(line);
    } else {
      buffer.push(line);
    }
  }

  closeGroup();
  flushText();

  return segments;
}

/** The Markdown without its step runs - what the table of contents lists. */
export function markdownWithoutSteps(markdown = '') {
  return splitStepGroups(markdown)
    .filter((segment) => segment.type === 'markdown')
    .map((segment) => segment.text)
    .join('\n');
}
